
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { LanguageSelector } from "./LanguageSelector";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";

interface DocumentTranslatorProps {
  documentId: string;
  documentPath: string;
  fileName: string;
  tenderId: string;
  isOpen: boolean;
  onClose: () => void;
}

export function DocumentTranslator({
  documentId,
  documentPath,
  fileName,
  tenderId,
  isOpen,
  onClose,
}: DocumentTranslatorProps) {
  const [targetLanguage, setTargetLanguage] = useState("en");
  const [translationInProgress, setTranslationInProgress] = useState(false);
  const [translationStatus, setTranslationStatus] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation("offerManagement");
  const currentLanguage = i18n.language;

  const handleCheckTranslationStatus = async () => {
    console.log("Checking translation status for document:", documentId);
    
    try {
      const { data, error } = await supabase
        .from("translated_documents")
        .select("*")
        .eq("original_document_id", documentId)
        .eq("target_language", targetLanguage);
      
      console.log("Translation status check result:", { data, error });
      
      if (error) {
        console.error("Error checking translation status:", error);
        return;
      }
      
      if (data && data.length > 0) {
        const translatedDoc = data[0];
        console.log("Found existing translation:", translatedDoc);
        
        if (translatedDoc.status === "completed") {
          toast({
            title: t("translations.alreadyTranslated"),
            description: t("translations.documentAlreadyTranslatedDesc"),
          });
          onClose();
          return true;
        } else if (translatedDoc.status === "processing") {
          setTranslationStatus(t("translations.translationInProgress"));
          return false;
        }
      }
      return false;
    } catch (error) {
      console.error("Error checking translation status:", error);
      return false;
    }
  };
  
  const handleTranslate = async () => {
    try {
      // Check if translation already exists
      const alreadyTranslated = await handleCheckTranslationStatus();
      if (alreadyTranslated) return;
      
      setTranslationInProgress(true);
      setTranslationStatus(t("translations.preparingTranslation"));
      
      console.log("Starting document translation with params:", {
        documentId,
        documentPath,
        fileName,
        targetLanguage,
        tenderId
      });
      
      // Use Supabase's built-in function invoker which automatically handles auth
      const { data, error } = await supabase.functions.invoke(
        "translate-document",
        {
          body: {
            documentId,
            documentPath,
            fileName,
            tenderId,
            targetLanguage
          }
        }
      );
      
      console.log("Edge function response:", data);
      
      if (error) {
        console.error("Translation error:", error);
        throw new Error(error.message || "Translation failed");
      }
      
      // Update the translation status
      setTranslationStatus(t("translations.translationInProgress"));
      
      toast({
        title: t("translations.translationStarted"),
        description: t("translations.translationStartedDesc"),
      });
      
      queryClient.invalidateQueries({ queryKey: ["translated-documents", tenderId] });
      onClose();
    } catch (error) {
      console.error("Error in translation process:", error);
      toast({
        title: t("translations.translationFailed"),
        description: typeof error === 'object' && error !== null && 'message' in error 
          ? String(error.message) 
          : t("translations.translationFailedDesc"),
        variant: "destructive",
      });
      setTranslationInProgress(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("translations.translateDocument")}</DialogTitle>
          <DialogDescription>
            {t("translations.selectLanguage")}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="flex flex-col space-y-2">
            <div className="font-medium text-sm">{t("translations.fileName")}</div>
            <div className="text-sm text-gray-700 break-words">{fileName}</div>
          </div>
          
          <div className="flex flex-col space-y-2">
            <div className="font-medium text-sm">{t("translations.targetLanguage")}</div>
            <LanguageSelector 
              selectedLanguage={targetLanguage}
              onLanguageChange={setTargetLanguage}
              excludeLanguage={currentLanguage}
            />
          </div>
          
          {translationStatus && (
            <div className="bg-blue-50 text-blue-800 p-3 rounded-md text-sm">
              {translationStatus}
            </div>
          )}
        </div>
        
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={translationInProgress}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleTranslate} disabled={translationInProgress}>
            {translationInProgress ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("translations.translating")}
              </>
            ) : (
              t("translations.translate")
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
